import { useMutation } from "@tanstack/react-query";
import { Cell, internal, loadStateInit, SendMode } from "@ton/core";
import { mnemonicToPrivateKey } from "@ton/crypto";
import { getWalletContract } from "../utils/getWalletContract";
import { useTonClient } from "./useTonClient";
export const useSendTransaction = ({ network } = {}) => {
    const { data: tonClient } = useTonClient({ network });
    return useMutation({
        mutationKey: ["sendTransaction", network],
        mutationFn: async (options) => {
            if (!tonClient) {
                throw new Error("Ton client not found");
            }
            const keyPair = await mnemonicToPrivateKey(options.mnemonics);
            const WalletContract = getWalletContract(options.walletVersion);
            const wallet = WalletContract.create({
                workchain: 0,
                publicKey: keyPair.publicKey,
            });
            const contract = tonClient.open(wallet);
            const seqnoCurrent = await contract.getSeqno();
            // Проверяем максимальное число сообщений в транзакции в зависимости от версии кошелька. Вообще, эта проверка не обязательна, она проводится и на tonconnect client
            if (options.messages) {
                if (options.walletVersion === "V4" && options.messages.length > 4) {
                    throw new Error("Wallet version V4 supports a maximum of 4 messages per transaction.");
                }
                if (options.walletVersion === "V5R1" && options.messages.length > 255) {
                    throw new Error("Wallet version V5R1 supports a maximum of 255 messages per transaction.");
                }
            }
            let timeout = options.validUntil ?? Math.floor(Date.now() / 1000) + 60 * 5;
            const timeoutIsValid = timeout - Math.floor(Date.now() / 1000) <= 300;
            timeout = timeoutIsValid ? timeout : Math.floor(Date.now() / 1000) + 60 * 5;
            // Если задан массив сообщений, используем его для формирования multi-message транзакции.
            // Иначе, создаём одно сообщение на основе legacy-параметров.
            const messages = options.messages
                ? options.messages.map((msg) => internal({
                    value: msg.value,
                    to: msg.to,
                    body: msg.body ? Cell.fromBase64(msg.body) : undefined,
                    bounce: msg.bounce ?? false,
                    // Если это первое сообщение и кошелек не инициализирован, добавляем инициализацию.
                    init: toStateInit(msg.stateInit),
                }))
                : [
                    internal({
                        // Используем операторы non-null, так как для legacy-варианта value и to обязательны.
                        value: options.value,
                        to: options.to,
                        body: options.body ? Cell.fromBase64(options.body) : undefined,
                        bounce: false,
                    }),
                ];
            // @ts-expect-error too hard to create correct typing between different wallet versions
            const transfer = contract.createTransfer({
                seqno: seqnoCurrent,
                secretKey: keyPair.secretKey,
                sendMode: options.mode === undefined ? SendMode.PAY_GAS_SEPARATELY : options.mode,
                messages,
                timeout,
            });
            await contract.send(transfer);
            return transfer;
        },
    });
};
export const toStateInit = (stateInit) => {
    if (!stateInit) {
        return undefined;
    }
    const { code, data } = loadStateInit(Cell.fromBase64(stateInit).asSlice());
    return {
        code,
        data,
    };
};
//# sourceMappingURL=useSendTransaction.js.map